import { captureMessage } from '@sentry/nextjs';
import Cookies from 'universal-cookie';

import { COOKIE_USER_ID } from '@/constants/cookie.constants';
import { getBrowserId } from '@/general/components/Analytics/Analytics.helpers';

import { CarmaApiTrackingEvent, CarmaTrackingEvent } from './CarmaTrackingApi.types';

const cookies = new Cookies();
const beaconThrottle = 5000;

/**
 * The list events queued to send to our CDP
 */
const eventsQueue: CarmaApiTrackingEvent[] = [];

let lastSentAt = 0;
let pendingFlush: ReturnType<typeof setTimeout> | null = null;

export const getCarmaTrackingEventsQueue = () => [...eventsQueue];

export const initCarmaEventTracker = () => {
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handlePageUnload);
  }
};

export const removeCarmaEventTracker = () => {
  // Process the queue before unloading our tracker
  processQueue();
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handlePageUnload);
  }
};

export const pushCarmaEvent = (data: CarmaTrackingEvent) => {
  const event = enrichCarmaEvent(data);
  if (event) {
    eventsQueue.push(event);
  }
  scheduleProcessQueue();
};

/**
 *  Method in charge of sending all the pending events to our CDP
 */
const processQueue = (): void => {
  lastSentAt = Date.now();
  if (pendingFlush) {
    clearTimeout(pendingFlush);
    pendingFlush = null;
  }

  if (typeof navigator?.sendBeacon !== 'function' || eventsQueue.length === 0) {
    return;
  }

  const formData = new FormData();
  formData.append('events', JSON.stringify(eventsQueue));
  try {
    navigator.sendBeacon(`${process.env.NEXT_PUBLIC_BIZ_API_URL}/carma-data`, formData);
  } catch (error) {
    captureMessage(`Error sending beacon with: "${error}"`, 'warning');
  }
  // Empty the queue
  eventsQueue.length = 0;
};

const handlePageUnload = () => {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    processQueue();
  }
};

/**
 * Leading edge fires immediately, same as a plain throttle. But a plain throttle (see
 * `src/utils/throttle.ts`, which deliberately has no trailing call — fine for its scroll-handler
 * consumers) drops a call made inside the window outright: nothing sends it later unless some
 * unrelated future `pushCarmaEvent` happens to land after the window closes. For a beacon that
 * event might never come, leaving it queued for the rest of the session. So schedule exactly one
 * trailing flush per window instead, guaranteeing every event goes out within `beaconThrottle`.
 */
const scheduleProcessQueue = (): void => {
  const elapsed = Date.now() - lastSentAt;

  if (elapsed >= beaconThrottle) {
    processQueue();
    return;
  }

  if (pendingFlush) {
    return;
  }

  pendingFlush = setTimeout(() => {
    pendingFlush = null;
    processQueue();
  }, beaconThrottle - elapsed);
};

const enrichCarmaEvent = (data: CarmaTrackingEvent): CarmaApiTrackingEvent | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const origin_url = window.location.toString();
  const timestamp = new Date().toISOString();
  const browser_id = getBrowserId();
  const user_id = cookies.get(COOKIE_USER_ID) ?? undefined;
  return {
    ...data,
    origin_url,
    timestamp,
    browser_id,
    user_id,
  };
};
